import React, { useCallback, useRef, useState } from 'react';
import { namesAndIds } from '../../../../../../../types/shared/PrequalificationDataEntry';
import { useHomeAddress } from '../../../../../providers/HomeAddressProvider';
import { useHomeAddressError } from '../../../../../providers/HomeAddressErrorProvider';
import { useAutoFillFocus } from '../../../../../providers/AutoFillFocusProvider';
import { regexValidation } from '../../../../../../../utils/cob-utils';
import AddressField from '../../../../../../FieldContainers/AddressFields/AddressField/AddressField';
import { generateFieldErrorMessage } from '../../../../../../../utils/error-message';
import type { TAddressInputProps } from '../../Inputs.types';

export default function HomeAddressInput({ addressValidationCheck, checkRegex, orangeAsterisksOnMobile, disabled = false }: TAddressInputProps): JSX.Element {
    const regexContainer = regexValidation();
    const addressRef = useRef<HTMLInputElement>(null);
    const [storedValue, setStoredValue] = useState('');

    const {
        homeAddress: { HomeAddressLine1 },
        setSingleValue,
    } = useHomeAddress();
    const {
        homeAddressError: {
            HomeAddressLine1: { isInError, isValidValue, required },
        },
        dispatch,
    } = useHomeAddressError();

    const { autoFillFocusOnBlurHandler, autoFillFocusOnFocusHandler } = useAutoFillFocus();

    const setValue = useCallback(setSingleValue('HomeAddressLine1'), []);

    const setValid = useCallback((valid: boolean) => {
        dispatch({
            type: 'setValid',
            target: 'HomeAddressLine1',
            payload: valid,
        });
    }, []);

    const handleBlur = () => {
        autoFillFocusOnBlurHandler();
        addressValidationCheck();
        checkRegex('Home', 'mailing_address', HomeAddressLine1, undefined, 'HomeAddressLine1');    
    };

    return (
        <AddressField
            addressType='home'
            {...namesAndIds.HomeAddressLine1}
            ref={addressRef}
            required={required}
            value={HomeAddressLine1}
            setValueCallback={(value: string) => {
                const containsInvalidCharacters = regexContainer.disallow.invalid_address_characters.test(value);

                if (containsInvalidCharacters) {
                    value = value.replace(regexContainer.disallow.invalid_address_characters, '');
                }

                setStoredValue(value);
                setValue(value); 
            }}
            alternate
            valid={[isValidValue, setValid]}
            error={isInError}
            errorText={regexContainer.po_box_test.test(storedValue) ? 'Home Address cannot be a PO Box' : generateFieldErrorMessage('Home Street Address', 'missingValue')}
            requiredMessage={generateFieldErrorMessage('Home Street Address', 'missingValue')}
            invalidErrorMessage={generateFieldErrorMessage('Home Street Address', 'thirdParty', 'address')}
            onFocusCallback={() => {
                autoFillFocusOnFocusHandler();
            }}
            onBlurCallback={handleBlur}
            onKeyDownCallback={e => {
                if (e.key === 'Enter') {
                    e.preventDefault();
                }
            }}
            disabled={disabled}
            useOutsideValidation={isValidValue}
            useSuccessIcon
            showSuccessIcon={disabled || undefined}
            orangeAsterisksOnMobile={orangeAsterisksOnMobile}
        />
    );
}
